import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';

import { selectClient } from '@client/model';
import { clientsApi, clientKeys } from '@client/api/waterlily';

/**
 * The calc engine consumes `intakeSurvey.clientBirthDate` as a Date
 * (`.getUTCFullYear()` etc.). The API serializes it as an ISO string, so any
 * client object that goes through `calcClientDerivatives` needs the field
 * hydrated first. The primary client gets this for free via the existing
 * Redux pipeline; partner clients fetched directly via React Query do not.
 *
 * Applied via `select` rather than `queryFn` so it runs on every cache read —
 * the partner client query key (`clientKeys.detail(partnerClientId)`) is
 * shared with `usePartnerLockedFundingSources`, which doesn't hydrate. If we
 * only hydrated in `queryFn`, a cached response populated by the other hook
 * would still surface here as a string and crash the engine.
 */
function hydrateClientDates(client: Client | null | undefined): Client | null {
  if (!client) return null;
  const birthDate = client.intakeSurvey?.clientBirthDate;
  if (typeof birthDate !== 'string' || !birthDate) return client;
  return {
    ...client,
    intakeSurvey: {
      ...client.intakeSurvey,
      clientBirthDate: new Date(birthDate),
    },
  };
}

/**
 * Fetches the partner client when `partnerClientId` is set on the current
 * client. Unlike `usePartnerLockedFundingSources`, this hook is independent
 * of the `partnerFundingSplit` feature flag — its purpose is to surface the
 * partner client wherever the calc engine needs it (e.g. joint-policy
 * coverage in the plan comparison), regardless of whether the partner-
 * funding-split UX is enabled.
 */
export function usePartnerClient() {
  const client = useSelector(selectClient);
  const partnerClientId = client?.partnerClientId ?? null;

  const { data: partnerClient = null, isLoading } = useQuery({
    queryKey: clientKeys.detail(partnerClientId ?? ''),
    queryFn: async () => {
      const response = (await clientsApi.getById(partnerClientId!)) as unknown as ClientContainer;
      return response.client ?? null;
    },
    select: hydrateClientDates,
    enabled: !!partnerClientId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    partnerClient,
    isLoading: !!partnerClientId && isLoading,
  };
}
